/**
 * @file Utilitaires partages par les modules de comportement d'armes (emission des evenements de tir, faisceaux, selection de cibles sur un segment).
 */
import type { WeaponBehavior } from '@shared/enums';
import type { EntityId } from '@shared/types';
import type { SimContext } from '../../Context';
import { isShootable } from '../../combat/damage';
import type { With } from '../../ecs/World';
import { spawnProjectile } from '../projectiles';
import type { ShotContext } from '../WeaponBehavior';

export type Tgt = With<'target' | 'health' | 'collider'>;

/** Incremente le compteur de tirs du chat (le rendu declenche recul + flash) et annonce le tir. */
export const announceShot = (ctx: SimContext, shot: ShotContext, behavior: WeaponBehavior, projectileIds: EntityId[]): void => {
  shot.cat.cat.shotSeq++;
  ctx.bus.emit('ProjectileFired', {
    catId: shot.cat.id,
    slot: shot.weapon.slot,
    behavior,
    weaponDefId: shot.weapon.def.id,
    x: shot.originX,
    y: shot.originY,
    angle: shot.aimAngle,
    projectileIds,
  });
};

/** Cibles tirables dont le cercle croise le segment [A,B] epais de `halfWidth`, triees par distance depuis A. */
export const targetsOnSegment = (ctx: SimContext, ax: number, ay: number, bx: number, by: number, halfWidth: number): Tgt[] => {
  const out: Tgt[] = [];
  for (const id of ctx.rt.targetGrid.querySegment(ax, ay, bx, by, halfWidth)) {
    const e = ctx.rt.world.get(id);
    if (e && isShootable(ctx, e)) out.push(e);
  }
  return out;
};

/** Faisceau purement visuel (kind 'beam') + evenement BeamFired. */
export const spawnBeam = (ctx: SimContext, shot: ShotContext, behavior: WeaponBehavior, x1: number, y1: number, width: number, seconds: number): void => {
  spawnProjectile(ctx, {
    kind: 'beam',
    behavior,
    ownerId: shot.cat.id,
    weaponDefId: shot.weapon.def.id,
    slot: shot.weapon.slot,
    x: shot.originX,
    y: shot.originY,
    angle: Math.atan2(y1 - shot.originY, x1 - shot.originX),
    speed: 0,
    radius: width / 2,
    range: 0,
    damage: shot.weapon.damage,
    crit: false,
    life: seconds,
    beamEnd: { x: x1, y: y1 },
  });
  shot.cat.cat.shotSeq++;
  ctx.bus.emit('BeamFired', { catId: shot.cat.id, behavior, x0: shot.originX, y0: shot.originY, x1, y1, width, seconds });
};
